import { useCallback, useContext, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { itemKindClass, requireProcessTerms } from "../../../../api/content/processTermWords";
import { listWorkflows, listWorkflowsGiving } from "../../../../api/content/workflowsApi";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { useLoad } from "../../../../shared/loading/useLoad";
import { parseCollectionVersionRef } from "../../../../shared/references/contentReferences";
import { appPaths } from "../../../../shared/routing/appPaths";
import { useSubmission } from "../../../../shared/state/useSubmission";
import { useWorkspace } from "../../../../shell/workspace";
import AppButton from "../../../../ui/buttons/AppButton";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import ChoiceCard from "../../../../ui/forms/ChoiceCard";
import Field from "../../../../ui/forms/Field";
import FieldGroup from "../../../../ui/forms/FieldGroup";
import { FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { ChangeSetStepContext, type ChangeSetStep } from "../../../processAuthoring/draft/changeSetStep";
import { DraftGuardContext } from "../../../processAuthoring/draft/draftGuardContext";
import EditedItemEditor from "../../page/currentStep/EditedItemEditor";
import type { PreparationViewProps } from "../preparationView";
import { shippedWorkflowOf } from "../shippedWorkflow";
import { startAuthoring } from "../startCommands";
import type { EditIntent } from "../threadIntent";
import { authoringTitle, editedItemOfIntent, newItemFormPath, preparedMessage, type ItemIntent } from "./authorChoices";
import { useStepBeforeThread } from "./editStep";

/** The ways to make the item: by hand in its form, the agent in a free Thread, or a Workflow. */
const BY_HAND = "by-hand";
const WITH_THE_AGENT = "";

/**
 * The latest listing, with the Workflows module B would start from nothing at hand that make an item of the kind (F37).
 * A failed read of the kinds' classes fails the load, to retry; a kind with no class has no such Workflow.
 */
async function makingWorkflows(projectId: string, itemKind: ItemIntent["itemKind"], signal: AbortSignal) {
  const listing = await listWorkflows(projectId, null, signal);
  await requireProcessTerms();
  const made = itemKindClass(itemKind);
  return { ...listing, making: made === null ? new Set<string>() : await listWorkflowsGiving(projectId, listing.version, made, signal) };
}

/**
 * Making a new item or working on one (F37; SB-10 to SB-12, SB-16): one view for "New … with
 * the agent", "Ask the agent", "Write with the agent" and Edit. It offers the item's form (by
 * hand), the agent, and the Workflows module B would start that make the item's kind. The
 * prepared message names the item at its exact version; it is sent in a free Thread, or
 * answers the Workflow's first person step. An edit by hand shows the "Edit an item" step's
 * view right here: its save starts the Thread (ADR-0031 D2, WA-11). Asked for by hand
 * ("Bearbeiten …", F116), it is all the view shows.
 */
export default function AuthorPreparation({ intent, requestKey, onStarted }: PreparationViewProps<ItemIntent>) {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const { projectId, itemKind } = intent;
  const load = useCallback((signal: AbortSignal) => makingWorkflows(projectId, itemKind, signal), [projectId, itemKind]);
  const { state: listing, reload } = useLoad(load);
  const handPath = intent.kind === "author" ? newItemFormPath(intent) : null;
  const [way, setWay] = useState(intent.kind === "edit" && intent.way === "by_hand" ? BY_HAND : WITH_THE_AGENT);
  // Leaving the manual way closes its editor: the tab's draft guard asks first, as on leaving the page.
  const { requestClose } = useContext(DraftGuardContext);
  const choose = (next: string) => {
    if (way === BY_HAND && next !== BY_HAND) requestClose(() => { setWay(next); });
    else setWay(next);
  };
  const [message, setMessage] = useState(() => preparedMessage(intent, t));
  const { state: submission, submit } = useSubmission();
  const starting = submission.status === "submitting" || submission.status === "succeeded";

  if (listing.status !== "loaded") return <LoadStatus state={listing} loadingLabel={t("threads.prepare.run.loadingWorkflows")} onRetry={reload} />;
  // Shipped Workflows for one job (editing, memories) start from their own actions.
  const workflows = listing.value.workflows.filter((candidate) => candidate.purpose === null && listing.value.making.has(candidate.reference));
  // An edit by hand is always a Thread of the DMW library's "Edit an item" (ADR-0031 D2), which
  // every project pins (ADR-0037 D6); a new item by hand opens its form.
  const editWorkflow = intent.kind === "edit" ? shippedWorkflowOf(listing.value.workflows, "edit_item") : null;
  const byHandOffered = intent.kind === "edit" ? editWorkflow !== null : handPath !== null;
  const workflow = workflows.find((candidate) => candidate.reference === way) ?? null;
  const byHand = way === BY_HAND && byHandOffered;
  // "Bearbeiten …" (F116): the Thread view is the item's editor, with no choice of how before it.
  if (intent.kind === "edit" && intent.way === "by_hand" && editWorkflow !== null) {
    return <EditByHand intent={intent} title={authoringTitle(intent, t)} contentVersion={listing.value.version} requestKey={requestKey} onStarted={onStarted} />;
  }

  const start = () => {
    if (byHand && handPath !== null) {
      workspace.open(handPath);
      return;
    }
    submit(async () => {
      const thread = await startAuthoring({
        projectId,
        title: authoringTitle(intent, t),
        workflow: workflow?.reference ?? null,
        contentVersion: listing.value.version,
        message,
        requestKey,
      });
      onStarted(thread.threadId);
    });
  };

  return (
    <div className="space-y-5">
      {intent.kind === "edit" ? (
        <p className="text-sm">
          {t("threads.prepare.author.about", { kind: t(`authoring.ask.kinds.${intent.itemKind}`) })}{" "}
          <Link to={appPaths.item(intent.item)} className="app-link" title={intent.item}>{intent.name}</Link>
        </p>
      ) : null}
      {/* "Bearbeiten im Thread" (F116) asks for the agent: no choice of how, no list of Workflows. */}
      {intent.kind === "edit" && intent.way === "with_the_agent" ? null : (
      <FieldGroup legend={t("threads.prepare.author.how")} className="space-y-2" disabled={starting}>
        <ChoiceCard
          name={`${requestKey}-way`}
          checked={way === WITH_THE_AGENT}
          onChoose={() => { choose(WITH_THE_AGENT); }}
          title={t("threads.prepare.author.agent")}
          description={t("threads.prepare.author.agentHint")}
        />
        {byHandOffered ? (
          <ChoiceCard
            name={`${requestKey}-way`}
            checked={way === BY_HAND}
            onChoose={() => { choose(BY_HAND); }}
            title={t("threads.prepare.author.byHand")}
            description={t("threads.prepare.author.byHandHint")}
          />
        ) : null}
        {workflows.map((candidate) => (
          <ChoiceCard
            key={candidate.reference}
            name={`${requestKey}-way`}
            checked={way === candidate.reference}
            onChoose={() => { choose(candidate.reference); }}
            title={candidate.name}
            description={candidate.description}
          />
        ))}
      </FieldGroup>
      )}
      {byHand && intent.kind === "edit" ? (
        <EditByHand intent={intent} title={authoringTitle(intent, t)} contentVersion={listing.value.version} requestKey={requestKey} onStarted={onStarted} />
      ) : null}
      {byHand ? null : (
        <Field label={t("threads.prepare.author.message")} hint={workflow === null ? t("threads.prepare.author.messageHint") : t("threads.prepare.author.answerHint")}>
          {(control) => (
            <textarea {...control} className={`${FORM_CONTROL_CLASS} font-mono text-xs`} rows={8} value={message} disabled={starting} onChange={(event) => { setMessage(event.target.value); }} />
          )}
        </Field>
      )}
      {byHand && intent.kind === "edit" ? (
        editWorkflow === null ? null : <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.author.byHandThread", { workflow: editWorkflow.name })}</p>
      ) : (
        <>
          <p className={FORM_HELP_TEXT_CLASS}>{byHand ? t("threads.prepare.author.byHandNote") : t("threads.prepare.run.title", { title: authoringTitle(intent, t) })}</p>
          {submission.status === "failed" ? <ErrorNotice error={submission.error} /> : null}
          <div className="flex justify-end">
            <AppButton variant="primary" size="sm" onClick={start} disabled={starting || (!byHand && message.trim() === "")}>
              {starting ? t("threads.prepare.starting") : byHand ? t("threads.prepare.author.openForm") : t("threads.prepare.run.start")}
            </AppButton>
          </div>
        </>
      )}
    </div>
  );
}

interface EditByHandProps {
  readonly intent: EditIntent;
  readonly title: string;
  /** The model version the preparation lists, which the Thread binds. */
  readonly contentVersion: string;
  readonly requestKey: string;
  readonly onStarted: (threadId: string) => void;
}

/**
 * The "Edit an item" step's view before its Thread: the save starts it, then the Thread opens in
 * place of the preparation, once the saved editor has left, so its draft guard has nothing to ask.
 */
function EditByHand({ intent, title, contentVersion, requestKey, onStarted }: EditByHandProps) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState<string | null>(null);
  const beforeThread = useStepBeforeThread({ projectId: intent.projectId, title, edited: editedItemOfIntent(intent), requestKey });
  const step = useMemo<ChangeSetStep>(
    () => ({
      shownIn: beforeThread.shownIn,
      save: async (projectId, choice, builders) => {
        const saved = await beforeThread.save(projectId, choice, builders);
        if (saved.kind === "answered") setOpened(saved.threadId);
        return saved;
      },
    }),
    [beforeThread],
  );
  // Once per Thread: the page's own `onStarted` is new on each of its renders.
  const open = useEffectEvent((threadId: string) => { onStarted(threadId); });
  useEffect(() => { if (opened !== null) open(opened); }, [opened]);
  const commit = parseCollectionVersionRef(contentVersion)?.commit ?? null;
  if (opened !== null) return <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.starting")}</p>;
  if (commit === null) return <p className={FORM_HELP_TEXT_CLASS}>{t("threads.editStep.noItem")}</p>;
  return (
    <ChangeSetStepContext.Provider value={step}>
      <EditedItemEditor edited={editedItemOfIntent(intent)} projectId={intent.projectId} contentCommit={commit} carry={intent.carry ?? null} />
    </ChangeSetStepContext.Provider>
  );
}
